import React, { useEffect } from "react";
import {
  Box,
  LinearProgress,
  Typography,
  Button,
  styled,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const CreditBannerContainer = styled(Box)(({ theme }) => ({
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.appBar + 1,
  background: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const CreditBannerContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  maxWidth: 1200,
  margin: "0 auto",
  padding: theme.spacing(1.5, 2),
  gap: theme.spacing(2),
}));

const CreditsBanner = ({ credits, setCredits }) => {
  const navigate = useNavigate();
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        setCredits((prev) => ({ ...prev, loading: true })); // Ensure loading starts
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/credit`,
        );
        setCredits({
          remaining: data.remaining_credits || 0,
          availed: data.availed_credits || 0,
          total: data.total_credits || 0,
          loading: false,
        });
      } catch (error) {
        console.error("Error fetching credits:", error);
        setCredits((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchCredits();
  }, []);

  return (
    <CreditBannerContainer>
      <CreditBannerContent>
        <Box display="flex" alignItems="center" gap={1.5}>
          {credits.loading ? (
            <LinearProgress sx={{ width: "100%" }} />
          ) : (
            <>
              <CheckCircleOutlineIcon
                color={credits.remaining > 0 ? "success" : "error"}
                fontSize="small"
              />
              <Typography variant="subtitle2" fontWeight={500}>
                {credits.remaining > 0 ? (
                  <>
                    <Box component="span" color="success.main" fontWeight={600}>
                      {credits.remaining}
                    </Box>
                    {` of ${credits.total} credits remaining`}
                  </>
                ) : (
                  <Box component="span" color="error.main">
                    All {credits.total} credits used
                  </Box>
                )}
              </Typography>
            </>
          )}
        </Box>

        <Box sx={{ flex: 1, maxWidth: 400, position: "relative" }}>
          {credits.loading ? (
            <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
          ) : (
            <>
              <LinearProgress
                variant="determinate"
                value={100}
                color="inherit"
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "divider",
                  position: "absolute",
                  width: "100%",
                }}
              />
              <LinearProgress
                variant="determinate"
                value={(credits.remaining / credits.total) * 100}
                color={credits.remaining > 0 ? "primary" : "error"}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  position: "relative",
                }}
              />
            </>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {!credits.loading && (
            <Button
              variant="contained"
              size="small"
              sx={{
                px: 2,
                textTransform: "none",
                fontWeight: 500,
                backgroundColor: "#FFD700", // Bright yellow color
                color: "#000", // Black text for contrast
                "&:hover": {
                  backgroundColor: "#FFC000", // Slightly darker yellow on hover
                },
              }}
              onClick={() => navigate("/pricing")}
            >
              {credits.remaining === 0 ? "Get More Credits" : "Upgrade Plan"}
            </Button>
          )}
        </Box>
      </CreditBannerContent>
    </CreditBannerContainer>
  );
};


export default CreditsBanner;
